import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { ThemedText } from '@/components/themed-text';
import { AnimatedView } from '@/components/ui/animated';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useThemedHex } from '@/hooks/use-theme-colors';

const WEEKDAY_HEADERS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const CELL_HEIGHT = 44;

interface Props {
  /** Dias com vaga (chaves 'YYYY-MM-DD'). Os demais ficam apagados. */
  availableDates: Set<string>;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  /** Limites navegáveis, derivados da janela de disponibilidade. */
  minDate: string;
  maxDate: string;
  todayKey: string;
  brand: string;
  /** Dias que já vão ficar marcados (ex.: grade recorrente escolhida). */
  markedDates?: Set<string>;
}

function keyToDate(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function dateToKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

function addDays(key: string, days: number): string {
  const date = keyToDate(key);
  date.setDate(date.getDate() + days);
  return dateToKey(date);
}

function addMonths(key: string, months: number): string {
  const date = keyToDate(key);
  date.setDate(1);
  date.setMonth(date.getMonth() + months);
  return dateToKey(date);
}

/** Domingo da semana que contém a data. */
function weekStart(key: string): string {
  return addDays(key, -keyToDate(key).getDay());
}

/** Sete dias a partir do domingo da semana. */
function weekCells(key: string): (string | null)[] {
  const start = weekStart(key);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** Grade do mês: 7 colunas fixas, com buracos antes do dia 1 e depois do último. */
function monthCells(key: string): (string | null)[] {
  const date = keyToDate(key);
  const year = date.getFullYear();
  const monthIndex = date.getMonth();
  const startOffset = new Date(year, monthIndex, 1).getDay();
  const total = new Date(year, monthIndex + 1, 0).getDate();
  const cells: (string | null)[] = Array.from({ length: startOffset }, () => null);
  for (let day = 1; day <= total; day += 1) {
    cells.push(dateToKey(new Date(year, monthIndex, day)));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/**
 * Calendário do agendamento: por padrão mostra só a semana do dia escolhido e
 * expande para o mês inteiro ao toque. Navegação anda de semana (recolhido) ou
 * de mês (expandido), sempre dentro da janela de disponibilidade.
 */
export function ScheduleCalendar({
  availableDates,
  selectedDate,
  onSelectDate,
  minDate,
  maxDate,
  todayKey,
  brand,
  markedDates,
}: Props) {
  const th = useThemedHex();
  const [expanded, setExpanded] = useState(false);
  const [anchor, setAnchor] = useState(selectedDate ?? todayKey);

  // O calendário acompanha o dia escolhido (inclusive quando vem de fora).
  useEffect(() => {
    if (selectedDate) setAnchor(selectedDate);
  }, [selectedDate]);

  // A grade renderizada é sempre o mês inteiro; recolhido mostra só a linha da
  // semana da âncora (altura de 1 linha + conteúdo deslocado até essa linha).
  // Assim a abertura/fechamento anima a altura de verdade, sem trocar conteúdo
  // nem "pular" (o cabeçalho e os dias da semana ficam fora da animação).
  const monthGrid = useMemo(() => monthCells(anchor), [anchor]);
  const monthHeight = (monthGrid.length / 7) * CELL_HEIGHT;
  const weekRowIndex = useMemo(() => {
    const i = monthGrid.indexOf(anchor);
    return i >= 0 ? Math.floor(i / 7) : 0;
  }, [monthGrid, anchor]);
  const collapsedTranslate = -weekRowIndex * CELL_HEIGHT;

  const progress = useSharedValue(expanded ? 1 : 0);
  useEffect(() => {
    progress.value = withTiming(expanded ? 1 : 0, {
      duration: expanded ? 260 : 220,
      easing: Easing.inOut(Easing.ease),
    });
  }, [expanded, progress]);

  const clipStyle = useAnimatedStyle(() => ({
    height: CELL_HEIGHT + progress.value * (monthHeight - CELL_HEIGHT),
  }));
  const slideStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: collapsedTranslate * (1 - progress.value) }],
  }));

  // Só navega enquanto o período visível puder conter algum dia com vaga.
  const range = useMemo(() => {
    const visible = (expanded ? monthGrid : weekCells(anchor)).filter((c): c is string => !!c);
    return { first: visible[0], last: visible[visible.length - 1] };
  }, [expanded, monthGrid, anchor]);
  const canGoBack = range.first > minDate;
  const canGoForward = range.last < maxDate;

  const shift = (dir: 1 | -1) => {
    setAnchor((current) => (expanded ? addMonths(current, dir) : addDays(current, dir * 7)));
  };

  const toggle = () => setExpanded((v) => !v);

  const title = format(keyToDate(anchor), 'MMMM yyyy', { locale: ptBR });

  return (
    <View
      className="rounded-2xl border border-neutral-200 bg-card p-3"
      style={{ borderCurve: 'continuous' }}
    >
      {/* Mês + navegação */}
      <View className="flex-row items-center justify-between px-1 pb-2">
        <PressableScale
          haptic
          onPress={toggle}
          hitSlop={6}
          className="flex-row items-center gap-1.5"
          accessibilityLabel={expanded ? 'Ver só a semana' : 'Ver o mês inteiro'}
        >
          <ThemedText className="font-bold text-sm capitalize text-neutral-900">{title}</ThemedText>
          <Ionicons
            className="text-neutral-500"
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={15}
          />
        </PressableScale>

        <View className="flex-row items-center">
          <PressableScale
            haptic
            disabled={!canGoBack}
            onPress={() => shift(-1)}
            hitSlop={8}
            className="h-8 w-8 items-center justify-center rounded-full"
            accessibilityLabel={expanded ? 'Mês anterior' : 'Semana anterior'}
          >
            <Ionicons
              name="chevron-back"
              size={18}
              color={canGoBack ? th('#525252') : th('#D4D4D4')}
            />
          </PressableScale>
          <PressableScale
            haptic
            disabled={!canGoForward}
            onPress={() => shift(1)}
            hitSlop={8}
            className="h-8 w-8 items-center justify-center rounded-full"
            accessibilityLabel={expanded ? 'Próximo mês' : 'Próxima semana'}
          >
            <Ionicons
              name="chevron-forward"
              size={18}
              color={canGoForward ? th('#525252') : th('#D4D4D4')}
            />
          </PressableScale>
        </View>
      </View>

      {/* Cabeçalho dos dias da semana */}
      <View className="flex-row">
        {WEEKDAY_HEADERS.map((label, index) => (
          <View key={index} className="items-center" style={{ width: `${100 / 7}%` }}>
            <ThemedText className="font-semibold text-[11px] uppercase text-neutral-400">
              {label}
            </ThemedText>
          </View>
        ))}
      </View>

      {/* Grade — altura animada nos dois sentidos (semana ↔ mês). O conteúdo é
          sempre o mês; recolhido desliza até a linha da semana da âncora. */}
      <AnimatedView style={[{ overflow: 'hidden' }, clipStyle]}>
        <AnimatedView style={[{ flexDirection: 'row', flexWrap: 'wrap' }, slideStyle]}>
          {monthGrid.map((date, index) => {
            if (!date) {
              return (
                <View
                  key={`empty-${index}`}
                  style={{ width: `${100 / 7}%`, height: CELL_HEIGHT }}
                />
              );
            }
            const enabled = availableDates.has(date);
            const selected = date === selectedDate;
            const marked = !selected && !!markedDates?.has(date);
            const isToday = date === todayKey;
            const dayNumber = Number(date.slice(8));

            return (
              <View
                key={date}
                className="items-center justify-center"
                style={{ width: `${100 / 7}%`, height: CELL_HEIGHT }}
              >
                <PressableScale
                  haptic={enabled}
                  disabled={!enabled}
                  onPress={() => onSelectDate(date)}
                  className="items-center justify-center rounded-full"
                  accessibilityLabel={`Dia ${dayNumber}${enabled ? '' : ' sem horários'}`}
                  style={{
                    height: 36,
                    width: 36,
                    backgroundColor: selected ? brand : marked ? `${brand}1A` : 'transparent',
                    borderWidth: !selected && isToday ? 1 : 0,
                    borderColor: th('#D4D4D4'),
                  }}
                >
                  <ThemedText
                    className="text-[15px]"
                    style={{
                      color: selected
                        ? '#fff'
                        : marked
                          ? brand
                          : enabled
                            ? th('#171717')
                            : th('#D4D4D4'),
                      fontWeight: selected || marked || enabled ? '600' : '400',
                    }}
                  >
                    {dayNumber}
                  </ThemedText>
                </PressableScale>
              </View>
            );
          })}
        </AnimatedView>
      </AnimatedView>
    </View>
  );
}
